/** @jsx jsx */
import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';
import { withAnalyticsContext, useAnalyticsEvents } from '@atlaskit/analytics-next';
import { LinkButton } from '@atlaskit/button/new';
import { css } from '@atlaskit/css';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import FolderOpenIcon from '@atlaskit/icon/core/folder-open';
import { Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { getPortalGroupSettingsUrl } from '@atlassian/help-center-common-util/url/url';
import messages from '../messages';

interface CustomizePortalButtonGroupProps {
	projectKey: string;
}

const CustomizePortalButtonGroup = (props: CustomizePortalButtonGroupProps) => {
	const { formatMessage } = useIntl();
	const { projectKey } = props;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClickManagePortalGroups = () => {
		createAnalyticsEvent({
			actionSubjectId: 'managePortalGroupsButton',
			action: 'clicked',
			actionSubject: 'CustomizePortalButtonGroup',
			analyticsType: UI_EVENT_TYPE,
		}).fire();
	};

	return (
		<Flex gap="space.100">
			<div css={ButtonStyles}>
				<LinkButton
					href={getPortalGroupSettingsUrl(projectKey)}
					appearance="subtle"
					target="_blank"
					testId="manage-portal-groups-button"
					onClick={onClickManagePortalGroups}
					iconBefore={CustomizePortalButtonFolderOpenIcon}
				>
					<Text weight="medium" color="color.text.subtle">
						{formatMessage(messages.managePortalGroupsLabel)}
					</Text>
				</LinkButton>
			</div>
		</Flex>
	);
};

const CustomizePortalButtonFolderOpenIcon = (iconProps: NewCoreIconProps) => {
	const { formatMessage } = useIntl();
	return (
		<FolderOpenIcon
			{...iconProps}
			label={formatMessage(messages.managePortalGroupsLabel)}
			spacing="none"
			color={token('color.icon', '#44546F')}
		/>
	);
};

const ButtonStyles = css({
	borderStyle: 'solid',
	borderRadius: token('radius.small', '4px'),
	borderColor: token('color.border', '#0B122824'),
	borderWidth: token('border.width', '1px'),
	height: 'fit-content',
});

export default withAnalyticsContext({ component: 'CustomizePortalButtonGroup' })(
	CustomizePortalButtonGroup,
);
